import { createMemo, For, Show, type Accessor, type JSX } from 'solid-js';
import { EffectSyncControl, createEffectSyncModel, type EffectSyncModel } from '../components/EffectSyncControl';
import {
  AUDIO_REACTIVE_PACKAGE_ID,
  type EffectPackageControlValue,
  type EffectPackageItem,
  type EffectZoneNames,
} from '../lib/effectPackages';
import { isMusicRate } from '../lib/musicRate';
import {
  baselineLayer,
  CONSOLE_DRIVER_SOURCES,
  DEFAULT_STACK_DRIVER,
  reorderedStackIds,
  responsiveLayers,
  type ConsoleDriverSource,
  type EffectStackDriver,
  type EffectStackLayer,
  type EffectStackLayerPatch,
  type EffectStackPatch,
} from '../lib/effectStack';
import type {
  SettingsTreeAction,
  SettingsTreeChoice,
  SettingsTreeControl,
  SettingsTreeNode,
  SettingsTreePlus,
} from '../lib/settingsTree';
import { TEST_IDS, testIdFor, testIdOf } from '../testIds';

/**
 * The adapter for ONE data model: an audio stack — the one baseline and the reactive layers — as
 * tree nodes (S34).
 *
 * Audio reactive's own stack and every Auto VJ step's composed look are this same model, so both reach
 * the tree view through this one builder. A layer is a leaf: its head is its package picker (the name is
 * written once, there), its sync and driver sit above its package's own parameter rows, and Move up,
 * Move down and Remove are in its ⋯ menu.
 */
export interface AudioStackModel {
  /** The running audio-reactive effect (Audio reactive) or the step (Auto VJ). Empty while stopped. */
  effectId: Accessor<string>;
  items: Accessor<EffectPackageItem[]>;
  /** The stack as the card is showing it: the room's, plus whatever edit is in the air. */
  layers: Accessor<EffectStackLayer[]>;
  /**
   * The stack as the ROOM reports it, and the only thing a package picker is ever allowed to show.
   *
   * A picker renders an identity, and an identity the room has not confirmed is a claim this console
   * cannot make. It is also a trap of the control's own shape: a `<select>` fires `onChange` only when
   * its value CHANGES, so a picker already displaying the operator's un-landed choice can never send
   * that choice again, and one unreported release leaves the control dead for the rest of the session
   * (prod, Alchemy, 2026-09-18 — the console read Golden Breath while the room ran Edges).
   */
  reported: Accessor<EffectStackLayer[]>;
  zoneNames: Accessor<EffectZoneNames>;
  /** Whether a named stack gesture — `baseline`, `layer:<id>:effect` — is still unanswered. */
  inFlight: (gestureKey: string) => boolean;
  disabled: Accessor<boolean>;
  /** A composed Auto VJ step: no live clock, and edge move arrows are omitted. */
  compose?: boolean;
  /** Whether a parameter write at this settings address is still unanswered. */
  committing?: (path: string) => boolean;
  onPatch: (patch: EffectStackPatch, gestureKey: string, final: boolean) => void;
  /** Where the viewer goes when the layer they are on is removed. */
  afterRemove: (control: SettingsTreeControl) => void;
  /** The layer's id in the whole tree, when the stack sits inside a larger one (an Auto VJ step). */
  nodeId?: (layerId: string) => string;
}

const DRIVER_LABELS: Record<ConsoleDriverSource, string> = {
  beat: 'Beat',
  hit: 'Hit',
  energy: 'Energy',
};

function patchLayer(model: AudioStackModel, change: EffectStackLayerPatch, gestureKey: string, final: boolean): void {
  model.onPatch({ layer: change }, gestureKey, final);
}

function packageRates(item: EffectPackageItem | undefined) {
  const values = item?.parameters.musicRate?.values?.filter(isMusicRate);
  return values?.length ? values : undefined;
}

/** The test-id prefix every control of one layer shares (`testIdFor.audioLayer`). */
export function layerPrefix(id: string): string {
  return testIdFor.audioLayer(id);
}

/**
 * What to call a layer whose package this console has never seen.
 *
 * The installed package's own name is used whenever there is one. When there is not — a stack authored
 * against a package that has since been removed — the last part of the package name is title-cased, so
 * the row still says "Sweep" rather than showing the operator a raw internal name.
 */
export function layerName(item: EffectPackageItem | undefined, packageId: string): string {
  if (item?.name) return item.name;
  const tail = packageId.split('.').pop() ?? packageId;
  return tail.replace(/[-_]+/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

/** The zone word a layer's row carries: the two headings the stack has always had (B48). */
export function zoneWord(zone: EffectStackLayer['zone']): string {
  return zone === 'baseline' ? 'Baseline' : 'Reactive';
}

/**
 * Send a package pick, and hand the control straight back to what the ROOM has.
 *
 * A pick is a REQUEST, and leaving it drawn as the control's own value is what makes the control
 * unusable. A `<select>` fires `onChange` only when its value CHANGES, so a picker displaying an
 * un-landed choice can never send that choice again — and selected option markers cannot put it right,
 * because the room's accessor never moved. The room's answer is what moves the control; between the
 * pick and that answer the slot is marked in flight, so nothing about it is silent.
 */
function pickPackage(
  select: HTMLSelectElement,
  room: Accessor<string>,
  send: (effectId: string) => void,
): void {
  const effectId = select.value;
  select.value = room();
  send(effectId);
}

interface PackageOption {
  packageId: string;
  name: string;
}

/**
 * Everything a slot may be pointed at, always including whatever it is ALREADY running.
 *
 * The picker is where the layer's name is written, so a layer authored against something that has since
 * been removed or turned unavailable has to name itself there or it reads as some other effect entirely.
 * Only what this room can actually run is offered otherwise.
 */
function slotOptions(
  options: EffectPackageItem[],
  items: EffectPackageItem[],
  effectId: string,
): PackageOption[] {
  const listed = options.map((item) => ({
    packageId: item.packageId,
    name: item.name,
  }));
  if (!effectId || listed.some((entry) => entry.packageId === effectId)) return listed;
  const item = items.find((entry) => entry.packageId === effectId);
  return [...listed, { packageId: effectId, name: layerName(item, effectId) }];
}

/** Preserve option node identity when a richer catalog says the same names in the same order. */
function samePackageOptions(left: PackageOption[], right: PackageOption[]): boolean {
  return left.length === right.length && left.every((option, index) => {
    const next = right[index];
    return next != null
      && option.packageId === next.packageId
      && option.name === next.name;
  });
}

/** Everything this room can actually run in a layer: its installed packages and its reactions. */
function runnable(items: EffectPackageItem[]): EffectPackageItem[] {
  return items.filter((item) => item.available && item.packageId !== AUDIO_REACTIVE_PACKAGE_ID);
}

/**
 * One menu holding everything the room can render: the installed packages, then the room's built-in
 * reactions in one group under the owner's own word for them, "Audio reactions" (B48).
 */
function PackageOptions(props: {
  options: Accessor<PackageOption[]>;
  /** The ROOM's selected package. Every option derives its selected state from this when it mounts. */
  selected: Accessor<string>;
  /** The word for an empty slot, when the slot may be empty at all. */
  empty?: string;
}): JSX.Element {
  return (
    <>
      <Show when={props.empty != null}>
        <option value="" selected={props.selected() === ''}>{props.empty}</option>
      </Show>
      <For each={props.options()}>
        {(item) => (
          <option value={item.packageId} selected={props.selected() === item.packageId}>{item.name}</option>
        )}
      </For>
    </>
  );
}

/**
 * The one baseline slot, with **None** always among its answers (B49).
 *
 * A replacement rides `baseline`, so the controller keeps the layer's id and the viewer's selection
 * does not dangle under their hand. It shows what the ROOM has, and while a pick is unanswered it shows
 * that same thing and takes no further picks.
 */
function BaselinePicker(props: {
  options: Accessor<PackageOption[]>;
  model: AudioStackModel;
  /** Told the tree id of the baseline this picker has just ADDED (the stack had none). */
  onAdded?: (nodeId: string | undefined) => void;
}): JSX.Element {
  const chosen = createMemo(() => baselineLayer(props.model.reported())?.effectId ?? '');
  const changing = createMemo(() => props.model.inFlight('baseline'));
  return (
    <select
      class="audio-stack-package"
      aria-label="Baseline"
      data-testid={TEST_IDS.audioStackBaselineEffect}
      aria-busy={changing()}
      disabled={props.model.disabled() || changing()}
      value={chosen()}
      onChange={(event) => pickPackage(event.currentTarget, chosen, (effectId) => {
        const added = patchStack(props.model, { baseline: effectId ? { effectId } : null }, 'baseline');
        if (effectId && props.onAdded) props.onAdded(added);
      })}
    >
      <PackageOptions options={props.options} selected={chosen} empty="None" />
    </select>
  );
}

/** A layer's head: the package it runs, as the picker that changes it. */
function LayerPicker(props: { id: string; model: AudioStackModel }): JSX.Element {
  const layer = createMemo(() => props.model.layers().find((entry) => entry.id === props.id));
  /**
   * The package the ROOM runs in this slot, never the one the viewer has only just asked for. A layer
   * the room has no opinion about is one the viewer has just ADDED, and it names what they added.
   */
  const chosen = createMemo(() => {
    const room = props.model.reported().find((entry) => entry.id === props.id);
    return (room ?? layer())?.effectId ?? '';
  });
  const changing = createMemo(() => props.model.inFlight(`layer:${props.id}:effect`));
  const options = createMemo<PackageOption[]>((previous) => {
    const next = slotOptions(runnable(props.model.items()), props.model.items(), chosen());
    return previous && samePackageOptions(previous, next) ? previous : next;
  }, []);
  return (
    <Show
      when={layer()?.zone === 'baseline'}
      fallback={(
        <select
          class="audio-stack-package"
          aria-label="Effect"
          data-testid={testIdOf('layerEffect', { prefix: layerPrefix(props.id) })}
          aria-busy={changing()}
          disabled={props.model.disabled() || changing()}
          value={chosen()}
          onChange={(event) => pickPackage(event.currentTarget, chosen, (effectId) => patchLayer(
            props.model, { id: props.id, effectId }, `layer:${props.id}:effect`, true,
          ))}
        >
          <PackageOptions options={options} selected={chosen} />
        </select>
      )}
    >
      <BaselinePicker options={options} model={props.model} />
    </Show>
  );
}

/**
 * A layer's controls that are not its package's parameters: its beat lock, and — reactive layers
 * only — what pushes it. How the stack combines is not among them: that is one setting on the whole
 * look (B38).
 */
function LayerControls(props: { id: string; model: AudioStackModel }): JSX.Element {
  const prefix = layerPrefix(props.id);
  const layer = createMemo(() => props.model.layers().find((entry) => entry.id === props.id));
  const driver = createMemo<EffectStackDriver>(() => layer()?.driver ?? DEFAULT_STACK_DRIVER);

  const sync = createEffectSyncModel({
    effectId: () => (props.model.effectId() ? `${props.model.effectId()}:${props.id}` : ''),
    reported: () => (props.model.effectId() && layer()
      ? { on: layer()!.sync.enabled, musicRate: layer()!.sync.musicRate }
      : undefined),
    remembered: () => (layer()
      ? { on: layer()!.sync.enabled, musicRate: layer()!.sync.musicRate }
      : undefined),
    onChange: (field, value) => patchLayer(props.model,
      { id: props.id, sync: field === 'enabled'
        ? { enabled: value as boolean } : { musicRate: value as EffectStackLayer['sync']['musicRate'] } },
      `layer:${props.id}:sync:${field}`, true,
    ),
  });
  const composedSync: EffectSyncModel = {
    enabled: () => layer()?.sync.enabled ?? false,
    musicRate: () => layer()?.sync.musicRate ?? 'bar',
    hasInput: () => true,
    listening: () => true,
    status: () => undefined,
    setEnabled: (enabled) => patchLayer(props.model,
      { id: props.id, sync: { enabled } }, `layer:${props.id}:sync`, true,
    ),
    setMusicRate: (musicRate) => patchLayer(props.model,
      { id: props.id, sync: { musicRate } }, `layer:${props.id}:sync`, true,
    ),
  };

  function patchDriver(change: Partial<EffectStackDriver>, key: string, final: boolean): void {
    patchLayer(props.model, { id: props.id, driver: change }, `layer:${props.id}:driver:${key}`, final);
  }

  return (
    <Show when={layer()}>
      {(current) => (
        <div class="audio-stack-settings" data-testid={TEST_IDS.audioStackSettings}>
          <EffectSyncControl
            model={props.model.compose ? composedSync : sync}
            description=""
            idPrefix={prefix}
            rates={packageRates(props.model.items().find((item) => item.packageId === current().effectId))}
            controlsEnabled={() => !props.model.disabled()}
            showStatus={!props.model.compose}
          />

          <Show when={current().zone === 'reactive'}>
            <div class="audio-stack-field">
              <span>Driver</span>
              <div class="effect-package-segmented" role="radiogroup" aria-label="Driver">
                <For each={CONSOLE_DRIVER_SOURCES}>
                  {(source) => (
                    <button
                      type="button"
                      role="radio"
                      classList={{ selected: driver().source === source }}
                      aria-checked={driver().source === source}
                      disabled={props.model.disabled()}
                      data-testid={testIdOf('layerDriver', { prefix, source })}
                      onClick={() => patchDriver({ source }, 'source', true)}
                    >{DRIVER_LABELS[source]}</button>
                  )}
                </For>
              </div>
            </div>

            <div class="audio-stack-driver" data-testid={testIdOf('layerSensitivity', { prefix })}>
              <label>
                <span>Threshold</span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={driver().threshold}
                  disabled={props.model.disabled()}
                  aria-label="Threshold"
                  data-testid={testIdOf('layerThreshold', { prefix })}
                  onInput={(event) => patchDriver({ threshold: event.currentTarget.valueAsNumber }, 'threshold', false)}
                  onChange={(event) => patchDriver({ threshold: event.currentTarget.valueAsNumber }, 'threshold', true)}
                />
              </label>
              <label>
                <span>Amount</span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={driver().amount}
                  disabled={props.model.disabled()}
                  aria-label="Amount"
                  data-testid={testIdOf('layerAmount', { prefix })}
                  onInput={(event) => patchDriver({ amount: event.currentTarget.valueAsNumber }, 'amount', false)}
                  onChange={(event) => patchDriver({ amount: event.currentTarget.valueAsNumber }, 'amount', true)}
                />
              </label>
              <label>
                <span>Attack</span>
                <input
                  type="number"
                  min="0"
                  max="30"
                  step="0.01"
                  value={driver().attack}
                  disabled={props.model.disabled()}
                  aria-label="Attack"
                  data-testid={testIdOf('layerAttack', { prefix })}
                  onChange={(event) => patchDriver({ attack: event.currentTarget.valueAsNumber }, 'attack', true)}
                />
              </label>
              <label>
                <span>Release</span>
                <input
                  type="number"
                  min="0"
                  max="30"
                  step="0.01"
                  value={driver().release}
                  disabled={props.model.disabled()}
                  aria-label="Release"
                  data-testid={testIdOf('layerRelease', { prefix })}
                  onChange={(event) => patchDriver({ release: event.currentTarget.valueAsNumber }, 'release', true)}
                />
              </label>
            </div>
          </Show>
        </div>
      )}
    </Show>
  );
}

/**
 * Send one stack edit and return the tree id of the layer it ADDED, if it added one.
 *
 * The id is minted where the edit is applied (the sender for Audio reactive, the step's draft for Auto
 * VJ), so what the edit made is read back as the one layer that was not there before it. Nothing else
 * can land between the two reads: they are one synchronous step.
 */
function patchStack(model: AudioStackModel, patch: EffectStackPatch, gestureKey: string): string | undefined {
  const before = new Set(model.layers().map((layer) => layer.id));
  model.onPatch(patch, gestureKey, true);
  const added = model.layers().find((layer) => !before.has(layer.id));
  return added ? model.nodeId?.(added.id) ?? added.id : undefined;
}

/** Append a reactive layer running `effectId`: the stack's one add, from its picker or its plus. */
function addLayer(model: AudioStackModel, effectId: string): string | undefined {
  return effectId ? patchStack(model, { add: { effectId } }, `add:${effectId}`) : undefined;
}

/** Everything the stack's Add offers: the room's runnable packages, then its reactions in their group. */
function addOptions(model: AudioStackModel): PackageOption[] {
  return slotOptions(runnable(model.items()), model.items(), '');
}

/**
 * The stack's Add as a tree row's plus (B88): the same menu under the same name, and the same add.
 *
 * Its choices are a plain cache rather than a memo: the plus is built from whatever reactive scope first
 * reads a node, and a computation owned by that scope would be disposed with it (H41).
 */
export function audioStackPlus(model: AudioStackModel): SettingsTreePlus {
  let options: PackageOption[] = [];
  let choices: SettingsTreeChoice[] = [];
  return {
    label: 'Add',
    get disabled() { return model.disabled(); },
    get choices() {
      const next = addOptions(model);
      if (!samePackageOptions(options, next)) {
        options = next;
        choices = next.map((option) => ({
          value: option.packageId,
          name: option.name,
        }));
      }
      return choices;
    },
    run: (effectId) => addLayer(model, effectId ?? ''),
  };
}

/** The ids of a stack's layers in tree order: the baseline, then the reactive layers. */
export function stackLayerIds(layers: readonly EffectStackLayer[]): string[] {
  const baseline = baselineLayer(layers);
  return [...(baseline ? [baseline.id] : []), ...responsiveLayers(layers).map((layer) => layer.id)];
}

/**
 * The layer nodes of one stack. Each node is created once per layer id and reads everything through
 * getters, so an edit re-reads a value without rebuilding the row, the picker or a slider.
 */
export function createAudioStackNodes(model: AudioStackModel): (id: string) => SettingsTreeNode | undefined {
  const nodes = new Map<string, SettingsTreeNode>();
  const layerOf = (id: string): EffectStackLayer | undefined => model.layers().find((layer) => layer.id === id);
  const itemOf = (layer: EffectStackLayer | undefined): EffectPackageItem | undefined => (
    layer ? model.items().find((item) => item.packageId === layer.effectId) : undefined
  );

  function move(id: string, direction: -1 | 1): void {
    model.onPatch({ order: reorderedStackIds(model.layers(), id, direction), movedId: id }, 'order', true);
  }

  function create(id: string): SettingsTreeNode {
    const siblings = (): EffectStackLayer[] => responsiveLayers(model.layers());
    const first = (): boolean => siblings()[0]?.id === id;
    const last = (): boolean => siblings()[siblings().length - 1]?.id === id;
    // Created once per layer, so the menu keeps its entries (and their DOM) across every edit.
    const moveUp: SettingsTreeAction = {
      label: 'Move up', get disabled() { return model.disabled() || first(); }, run: () => move(id, -1),
    };
    const moveDown: SettingsTreeAction = {
      label: 'Move down', get disabled() { return model.disabled() || last(); }, run: () => move(id, 1),
    };
    const remove: SettingsTreeAction = {
      label: 'Remove',
      danger: true,
      get disabled() { return model.disabled(); },
      run: (control) => {
        model.onPatch({ remove: id }, `remove:${id}`, true);
        model.afterRemove(control);
      },
    };
    // A plain cache, not a memo: nodes are built lazily from whatever reactive scope first asks, and a
    // computation owned by that scope would be disposed with it.
    let shownActions: SettingsTreeAction[] = [];
    const reactiveActions = (): SettingsTreeAction[] => {
      const next = [
        ...(!model.compose || !first() ? [moveUp] : []),
        ...(!model.compose || !last() ? [moveDown] : []),
        remove,
      ];
      if (next.length !== shownActions.length || next.some((entry, index) => entry !== shownActions[index])) shownActions = next;
      return shownActions;
    };
    return {
      id: model.nodeId?.(id) ?? id,
      // The package the ROOM runs in this slot, as the picker shows it (B44); a layer the room has not
      // reported yet is one the viewer has just added, and it names what they added.
      get name() {
        const layer = model.reported().find((entry) => entry.id === id) ?? layerOf(id);
        return layer ? layerName(model.items().find((item) => item.packageId === layer.effectId), layer.effectId) : '';
      },
      get tag() { const layer = layerOf(id); return layer ? zoneWord(layer.zone) : undefined; },
      children: [],
      get parameters() { return itemOf(layerOf(id)); },
      get values() { return (layerOf(id)?.parameters ?? {}) as Record<string, EffectPackageControlValue>; },
      get synced() { return layerOf(id)?.sync.enabled === true; },
      get zoneNames() { return model.zoneNames(); },
      parameterPrefix: layerPrefix(id),
      onParameters: (parameters, key, final) => patchLayer(
        model, { id, parameters }, `layer:${id}:parameter:${key}`, final,
      ),
      committing: (key) => model.committing?.(`layers/${id}/parameters/${key}`) === true,
      get actions() { return layerOf(id)?.zone === 'reactive' ? reactiveActions() : []; },
      head: () => <LayerPicker id={id} model={model} />,
      controls: () => <LayerControls id={id} model={model} />,
      get disabled() { return model.disabled(); },
    };
  }

  return (id) => {
    if (!layerOf(id)) return undefined;
    let node = nodes.get(id);
    if (!node) {
      node = create(id);
      nodes.set(id, node);
    }
    return node;
  };
}

/**
 * What a stack adds beneath itself: the baseline slot while there is none (its None is the empty
 * answer, B49), and the one `Add` picker that appends a reactive layer.
 */
export function AudioStackAdd(props: {
  model: AudioStackModel;
  /** Told the tree id of the layer just added, so the tree can select it (B89). */
  onAdded?: (nodeId: string | undefined) => void;
}): JSX.Element {
  const menu = createMemo<PackageOption[]>((previous) => {
    const next = addOptions(props.model);
    return previous && samePackageOptions(previous, next) ? previous : next;
  }, []);
  let addPicker!: HTMLSelectElement;
  function add(effectId: string): void {
    if (!effectId) return;
    // The picker is an action, not a state: it goes back to resting the moment it has been used.
    addPicker.value = '';
    const added = addLayer(props.model, effectId);
    props.onAdded?.(added);
  }
  // Each picker stands under its zone's own word (B48), which is what says which question it answers.
  return (
    <>
      <Show when={!baselineLayer(props.model.layers())}>
        <div class="audio-stack-field">
          <span aria-hidden="true">Baseline</span>
          <BaselinePicker options={menu} model={props.model} onAdded={props.onAdded} />
        </div>
      </Show>
      <div class="audio-stack-field">
        <span aria-hidden="true">Reactive</span>
        <select
          ref={addPicker}
          class="audio-stack-package audio-stack-add"
          aria-label="Add"
          data-testid={TEST_IDS.audioStackReactiveAdd}
          disabled={props.model.disabled()}
          value=""
          onChange={(event) => add(event.currentTarget.value)}
        >
          <PackageOptions options={menu} selected={() => ''} empty="" />
        </select>
      </div>
    </>
  );
}
